import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import { type JSX } from 'react'
import { useMedplumProfile } from '@medplum/react'
import { Shell } from './components'
import { ProtectedRoute } from './components/ProtectedRoute'
import { SessionGuard, CallSessionProvider } from './components/session'
import {
  Login,
  ForgotPassword,
  SetPassword,
  CallPage,
  PatientList,
  PatientPage,
  ProviderList,
  ProviderPage,
  MyProfilePage,
  Reminders,
  ConsentsContracts,
  SuperHome,
  Organizations,
  ProviderEnroll,
  SettingsHome,
  InsurancePlans,
  SettingsTerminology,
  OrganizationConnects,
  OrganizationConnectAccept,
  HomePage,
  CallCenterAgents,
  Terminology,
  FaxPage,
  FaxSentPage,
  FaxInboxPage,
  FaxDetailPage,
  FaxSentDetailPage,
  FaxNotSelectedPage,
  DataMigrations,
  TestCatalog,
  Tasks,
  Cpg,
  AuthConsole,
  HealthcheckPage,
  AssistantPage,
  Integrations,
  PersonaPilot,
  MergedPatients,
} from './pages'

import { SchedulePage as SchedulePageV2 } from './pages/Schedule'
import { AccountingReportsPage, BillingLayout, BillingV2Page } from './pages/BillingV2'
import { ScheduleContextProvider as ScheduleContextProviderV2 } from './pages/Schedule/context'
import { MessagesPage } from './pages/Messages'
import { MessagesUnreadProvider } from './pages/Messages/MessagesUnreadContext'
import { PageErrorBoundary } from './components/PageErrorBoundary'
import { CallCenterHome } from './pages/CallCenterHome'
import { CareCoordinationPage } from './pages/CareCoordination'
import { PopulationRoute } from './pages/Population'
import { CrmRoute } from './pages/Crm'
import { DuplicateQueuePage } from './pages/PatientDuplicates/DuplicateQueuePage'
import { DuplicateReviewPage } from './pages/PatientDuplicates/DuplicateReviewPage'
import { useCallCenterManagerRole, type RoleCheckStatus } from './hooks/useCallCenterManagerRole'

// Legacy /boards deep links land on the Care Queue tab of Care Coordination.
function BoardsRedirect() {
  const { boardId } = useParams<{ boardId: string }>()
  return (
    <Navigate to={`/appointments?tab=care-queue${boardId ? `&board=${boardId}` : ''}`} replace />
  )
}

function HomeRoute({
  isCallCenterManager,
  roleStatus,
}: {
  isCallCenterManager: boolean
  roleStatus: RoleCheckStatus
}): JSX.Element | null {
  if (roleStatus === 'loading') {
    return null
  }

  return (
    <PageErrorBoundary feature="home">
      {isCallCenterManager ? <CallCenterHome /> : <HomePage />}
    </PageErrorBoundary>
  )
}

function ScheduleRoute(): JSX.Element {
  return (
    <PageErrorBoundary feature="schedule">
      <ScheduleContextProviderV2>
        <SchedulePageV2 />
      </ScheduleContextProviderV2>
    </PageErrorBoundary>
  )
}

export const AppRoutes = () => {
  const profile = useMedplumProfile()
  const { isCallCenterManager, status: roleStatus } = useCallCenterManagerRole(profile?.id)

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/resetpassword" element={<ForgotPassword />} />
      <Route path="/setpassword/:id/:secret" element={<SetPassword />} />
      {import.meta.env.VITE_HEALTHCHECK_PAGE === 'true' && (
        <Route path="/healthcheck" element={<HealthcheckPage />} />
      )}
      <Route
        path="/"
        element={
          <ProtectedRoute level="user">
            <SessionGuard>
              <CallSessionProvider>
                <MessagesUnreadProvider>
                  <Shell />
                </MessagesUnreadProvider>
              </CallSessionProvider>
            </SessionGuard>
          </ProtectedRoute>
        }
      >
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/fax" element={<FaxPage />}>
          <Route index element={<Navigate to="/fax/inbox" replace />} />
          <Route path="inbox" element={<FaxInboxPage />}>
            <Route index element={<FaxNotSelectedPage />} />
            <Route path=":taskId" element={<FaxDetailPage />} />
          </Route>
          <Route path="sent" element={<FaxSentPage />}>
            <Route index element={<FaxNotSelectedPage />} />
            <Route path=":messageId" element={<FaxSentDetailPage />} />
          </Route>
        </Route>
        <Route
          path="/appointments"
          element={
            <PageErrorBoundary feature="appointments">
              <CareCoordinationPage />
            </PageErrorBoundary>
          }
        />
        <Route
          path="/billing"
          element={
            <PageErrorBoundary feature="billing">
              <BillingLayout />
            </PageErrorBoundary>
          }
        >
          <Route index element={<BillingV2Page />} />
          <Route path="accounting" element={<AccountingReportsPage />} />
        </Route>
        {/* Behind the populationHealth LaunchDarkly flag, targeted per user. */}
        <Route path="/population" element={<PopulationRoute />} />
        {/* PD-1993: super admins and care coordinators. */}
        <Route path="/crm" element={<CrmRoute />} />
        <Route path="/patients" element={<PatientList />} />
        {/* PD-1986 duplicate review — before /patients/:patientId so "duplicates" is not read as an id. */}
        <Route
          path="/patients/duplicates"
          element={
            <PageErrorBoundary feature="patient-duplicates">
              <DuplicateQueuePage />
            </PageErrorBoundary>
          }
        />
        <Route
          path="/patients/duplicates/:reviewId"
          element={
            <PageErrorBoundary feature="patient-duplicates">
              <DuplicateReviewPage />
            </PageErrorBoundary>
          }
        />
        <Route
          path="/patients/:patientId"
          element={
            <PageErrorBoundary feature="patient">
              <PatientPage />
            </PageErrorBoundary>
          }
        >
          <Route path="*" element={<PatientPage />} />
        </Route>
        {/* Provider Operations — Care Coordinators and super admins only; the middleware's
            scope answer decides (docs/GLOSSARY.md "Provider Operations"). */}
        <Route
          path="/providers"
          element={
            <ProtectedRoute level="practitioner-ops">
              <PageErrorBoundary feature="providers">
                <ProviderList />
              </PageErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/providers/:practitionerId"
          element={
            <ProtectedRoute level="practitioner-ops">
              <PageErrorBoundary feature="providers">
                <ProviderPage />
              </PageErrorBoundary>
            </ProtectedRoute>
          }
        />
        {/* My Profile — the signed-in member's own record; the plain user level admits it
            (docs/GLOSSARY.md "My Profile"). */}
        <Route
          path="/profile"
          element={
            <PageErrorBoundary feature="profile">
              <MyProfilePage />
            </PageErrorBoundary>
          }
        />
        <Route
          path="/"
          element={<HomeRoute isCallCenterManager={isCallCenterManager} roleStatus={roleStatus} />}
        />

        <Route path="/schedule" element={<ScheduleRoute />} />
        <Route
          path="/call/:encounterId"
          element={
            <PageErrorBoundary feature="call">
              <CallPage />
            </PageErrorBoundary>
          }
        >
          <Route path="*" element={<CallPage />} />
        </Route>
        <Route
          path="/assistant"
          element={
            <PageErrorBoundary feature="assistant">
              <AssistantPage />
            </PageErrorBoundary>
          }
        />
        <Route path="/boards" element={<Navigate to="/appointments?tab=care-queue" replace />} />
        <Route path="/boards/:boardId" element={<BoardsRedirect />} />
        <Route path="/settings" element={<SettingsHome />} />
        <Route path="/settings/insurance-plans" element={<InsurancePlans />} />
        <Route path="/settings/terminology" element={<SettingsTerminology />} />
        <Route path="/settings/connects" element={<OrganizationConnects />} />
        <Route path="/settings/connects/accept/:id" element={<OrganizationConnectAccept />} />
      </Route>

      <Route
        path="/super"
        element={
          <ProtectedRoute level="system">
            <SessionGuard>
              <CallSessionProvider>
                <MessagesUnreadProvider>
                  <Shell />
                </MessagesUnreadProvider>
              </CallSessionProvider>
            </SessionGuard>
          </ProtectedRoute>
        }
      >
        <Route path="/super" element={<SuperHome />} />
        <Route path="/super/reminders" element={<Reminders />} />
        <Route path="/super/organizations" element={<Organizations />} />
        <Route path="/super/terminology" element={<Terminology />} />
        <Route path="/super/consents-contracts" element={<ConsentsContracts />} />
        <Route path="/super/call-center-agents" element={<CallCenterAgents />} />
        <Route path="/super/provider-enroll" element={<ProviderEnroll />} />
        <Route path="/super/data-migrations" element={<DataMigrations />} />
        <Route path="/super/test-catalog" element={<TestCatalog />} />
        <Route path="/super/tasks" element={<Tasks />} />
        <Route path="/super/cpg/:view?" element={<Cpg />} />
        <Route path="/super/auth/:tab?" element={<AuthConsole />} />
        <Route path="/super/merged-patients" element={<MergedPatients />} />
        {/* Ops page for the Zocdoc integration (docs/ZOCDOC_INTEGRATION_PLAN.md). Super admins
            only, which is what the /super layout's `level="system"` guard already enforces. */}
        <Route
          path="/super/integrations"
          element={
            <PageErrorBoundary feature="zocdoc-admin">
              <Integrations />
            </PageErrorBoundary>
          }
        />
        {/* Persona identity-verification pilot cohort (docs/PERSONA_IDV_PLAN.md step 2.2). */}
        <Route path="/super/persona-pilot" element={<PersonaPilot />} />
        <Route path="*" element={<SuperHome />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
